import React, { useState } from 'react';

/**
 * A wrapper component that automatically adds a loading state to any element
 * with an async `onClick` handler.
 * 
 * Usage:
 * <WithLoading>
 *   <div className="my-icon" onClick={async () => await save()}>
 *     <Icon />
 *   </div>
 * </WithLoading>
 */
const HANDLER_PROPS = [
  'onClick',
  'onConfirmClick',
  'onCancelClick',
  'onSubmit',
  'onChange',
  'onSave',
  'onArchiveSelected',
  'onDelete',
  'onActionClick',
  'onPrimaryClick',
  'onSecondaryClick',
  'onAddProducts',
  'onDuplicate',
  'onPrint',
  'onEmail',
  'onExport',
];

const WithLoading = ({ children, loadingProps = HANDLER_PROPS, timeoutMs = 10000 }) => {
  const [isLoading, setIsLoading] = useState(false);

  let child;
  try {
    child = React.Children.only(children);
  } catch (err) {
    console.error("WithLoading requires exactly one child element.");
    return children;
  }

  const wrapHandler = (originalHandler) => {
    if (!originalHandler) return undefined;

    return async (...args) => {
      if (isLoading) return;

      const result = originalHandler(...args);
      if (result instanceof Promise) {
        setIsLoading(true);
        let timeoutId;
        const timeoutPromise = new Promise((resolve) => {
          timeoutId = setTimeout(() => {
            resolve({ isTimeout: true });
          }, timeoutMs);
        });

        try {
          await Promise.race([result, timeoutPromise]);
        } catch (error) {
          // Promise rejected
        } finally {
          clearTimeout(timeoutId);
          setIsLoading(false);
        }
      }
    };
  };

  // Build overridden props only for handlers that exist on the child
  const overriddenHandlers = {};
  loadingProps.forEach((prop) => {
    if (child.props[prop]) {
      overriddenHandlers[prop] = wrapHandler(child.props[prop]);
    }
  });

  const loadingStyle = isLoading
    ? { opacity: 0.5, cursor: 'not-allowed' }
    : {};

  return React.cloneElement(child, {
    ...overriddenHandlers,
    disabled: child.props.disabled || isLoading,
    style: {
      ...child.props.style,
      ...loadingStyle,
    },
  });
};

export default WithLoading;